import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Loader — UI-2C2 shared, accessible loading indicator.
 *
 * Brand-aligned (violet) spinner with `role="status"` + screen-reader text.
 * Respects `prefers-reduced-motion` (spinner freezes, status is still announced).
 *
 * Props:
 * - size       "sm" | "default" | "lg"  spinner size (inline / section / page)
 * - label      string                   accessible loading text (announced)
 * - showLabel  boolean                  also render the label visibly
 * - fullScreen boolean                  centre within a min-h-screen warm surface
 */
const sizeMap = {
  sm: "w-4 h-4",
  default: "w-8 h-8",
  lg: "w-12 h-12",
};

export function Loader({
  size = "default",
  label = "Loading",
  showLabel = false,
  fullScreen = false,
  className,
  ...props
}) {
  const content = (
    <div
      role="status"
      aria-live="polite"
      data-testid="loader"
      className={cn("flex flex-col items-center justify-center gap-3", className)}
      {...props}
    >
      <Loader2
        className={cn(
          "animate-spin motion-reduce:animate-none text-violet-600",
          sizeMap[size] || sizeMap.default
        )}
        aria-hidden="true"
      />
      {showLabel && (
        <span className="text-sm text-[var(--text-muted)]">{label}</span>
      )}
      <span className="sr-only">{label}</span>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        {content}
      </div>
    );
  }
  return content;
}

export default Loader;
